import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { message, Spin, Pagination, Alert } from 'antd';
import { 
  InfoCircleOutlined,
  ArrowLeftOutlined, 
  PaperClipOutlined, 
  DownloadOutlined, 
  DeleteOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { SubColor } from '../style/styleValue';
import { 
  getReportList, registerReport, updateReport, deleteReport, 
  getReportData, deleteFile, downloadFile 
} from '../api/errorReportAPI';
import { getLoginUser } from '../api/user';

interface User {
  username: string;
  role: string;
}

interface ReportListItem {
  id: number;
  title: string;
  authorName: string;
  createdAt: string;
  viewCount: number;
  fileCount?: number;
}

interface ReportFile {
  id: number;
  originFileName: string;
  fileSize: number;
}

interface ReportDetail {
  id: number;
  title: string;
  content: string;
  authorName: string;
  viewCount: number;
  createdAt: string;
  modifiedAt: string;
  files: ReportFile[];
}

// 확장자가 이미지인지 확인하는 유틸 함수
const isImageFile = (fileName: string) => {
  return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
};

// 개별 이미지를 불러와서 렌더링하는 컴포넌트
const ImageViewer = ({ file, onDownload }: { file: ReportFile; onDownload: (file: ReportFile) => void }) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;
    const loadImage = async () => {
      try {
        const response = await downloadFile(file.id);
        objectUrl = window.URL.createObjectURL(new Blob([response]));
        setImgSrc(objectUrl);
      } catch (error) {
        console.error('이미지 로드 실패:', error);
      }
    };
    
    loadImage();

    return () => {
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [file.id]);

  return (
    <ImageWrapper>
      {imgSrc ? (
        <img src={imgSrc} alt={file.originFileName} />
      ) : (
        <Spin tip="이미지 불러오는 중..." style={{ margin: '50px 0' }} />
      )}
      <div className="image-overlay">
        <button onClick={() => onDownload(file)}>
          <DownloadOutlined /> 원본 다운로드
        </button>
      </div>
    </ImageWrapper>
  );
};

const ErrorReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryId = searchParams.get('id');
  const queryPage = searchParams.get('page');

  const [currentUser, setCurrentUser] = useState<User>({ username: "", role: "" });
  const [posts, setPosts] = useState<ReportListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [page, setPage] = useState(queryPage ? Number(queryPage) : 1);
  const [pageSize, setPageSize] = useState(10);

  const [isWriteModalOpen, setIsWriteModalOpen] = useState(false);
  const [viewPost, setViewPost] = useState<ReportDetail | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ title: '', content: '' });
  
  const [newFiles, setNewFiles] = useState<File[]>([]); 
  const [existingFiles, setExistingFiles] = useState<ReportFile[]>([]); 

  useEffect(() => {
    if (queryPage) setPage(Number(queryPage));
  }, [queryPage]);

  const fetchPosts = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getReportList(page, pageSize);
      setPosts(response.data); 
      setTotalCount(response.total); 
    } catch (error) {
      message.error('진단오류게시판 목록을 불러오는데 실패했습니다.');
    } finally {
      setListLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (queryId) {
      const fetchDetail = async () => {
        setDetailLoading(true);
        try {
          const data = await getReportData(Number(queryId));
          setViewPost(data);
        } catch (error) {
          console.error(error);
          message.error('상세 내용을 불러오는데 실패했습니다.');
          setSearchParams({ page: String(page) });
        } finally {
          setDetailLoading(false);
        }
      };
      fetchDetail();
    } else {
      setViewPost(null);
    }
  }, [queryId, setSearchParams]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => {
    getLoginUser().then(data => setCurrentUser({ username: data.name, role: data.role }));
  }, []);

  const handlePostClick = (post: ReportListItem) => {
    setSearchParams({ page: String(page), id: String(post.id), reportTitle: String(post?.title) });
  };

  const handleBackToList = () => {
    setSearchParams({ page: String(page) });
  };

  const handlePageChange = (newPage: number) => {
    setSearchParams({ page: String(newPage) });
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setSearchParams({ page: '1' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setNewFiles(prev => [...prev, ...selectedFiles]);
    }
    e.target.value = '';
  };

  const removeNewFile = (index: number) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingFile = async (fileId: number) => {
    if (window.confirm('해당 파일을 삭제하시겠습니까? (즉시 삭제됩니다)')) {
      const result = await deleteFile(fileId);
      if (result) {
        setExistingFiles(prev => prev.filter(f => f.id !== fileId));
        if (viewPost && viewPost.id === editingId) {
           const updatedPost = await getReportData(viewPost.id);
           setViewPost(updatedPost);
        }
        message.success('파일이 삭제되었습니다.');
      } else {
        message.error('파일 삭제 실패');
      }
    }
  };

  const handleWriteClick = () => {
    setEditingId(null);
    setFormData({ title: '', content: '' });
    setNewFiles([]);
    setExistingFiles([]);
    setIsWriteModalOpen(true);
  };

  const handleEditFromDetail = () => {
    if (!viewPost) return;
    setEditingId(viewPost.id);
    setFormData({ title: viewPost.title, content: viewPost.content });
    setNewFiles([]);
    setExistingFiles(viewPost.files || []);
    setIsWriteModalOpen(true);
  };

  const closeWriteModal = () => {
    setIsWriteModalOpen(false);
    setEditingId(null);
    setFormData({ title: '', content: '' });
    setNewFiles([]);
    setExistingFiles([]);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      message.warning('제목과 내용을 모두 입력해주세요.');
      return;
    }
    if (editingId) {
      const result = await updateReport(editingId, formData.title, formData.content, newFiles);
      if (result) {
        message.success('게시글이 수정되었습니다.');
        if (viewPost && viewPost.id === editingId) {
          const data = await getReportData(editingId);
          setViewPost(data);
        } else {
          fetchPosts();
        }
        closeWriteModal();
      } else {
        message.error('게시글 수정에 실패했습니다.');
      }
    } else {
      const result = await registerReport(formData.title, formData.content, newFiles);
      if (result) {
        message.success('제보가 등록되었습니다.');
        fetchPosts();
        closeWriteModal();
      } else {
        message.error('제보 등록에 실패했습니다.');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('정말로 이 게시글을 삭제하시겠습니까?')) {
      const result = await deleteReport(id);
      if (result) {
        message.success('게시글이 삭제되었습니다.');
        handleBackToList();
        fetchPosts();
      } else {
        message.error('게시글 삭제에 실패했습니다.');
      }
    }
  };

  const handleDownload = async (file: ReportFile) => {
    try {
      const response = await downloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      message.error('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Container>
      <AdminInfoBox>
        <Alert
          message="진단오류제보게시판 안내"
          description="해당 게시판에 제보글 작성 시 자문료가 지급됩니다."
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
        />
      </AdminInfoBox>
      {!queryId && !detailLoading ? (
        <>
          <TopBar>
            <TotalCount>총 <span>{totalCount}</span>개</TotalCount>
            <SelectBox onChange={handlePageSizeChange} value={pageSize}>
              <option value="10">10개</option>
              <option value="20">20개</option>
              <option value="30">30개</option>
            </SelectBox>
          </TopBar>

          <Table>
            <thead>
              <tr>
                <Th width="8%">번호</Th>
                <Th width="50%">제목</Th>
                <Th width="12%">작성자</Th>
                <Th width="12%">등록일</Th>
              </tr>
            </thead>
            <tbody>
              {listLoading ? (
                 <tr><Td colSpan={6} style={{ padding: '50px' }}><Spin /></Td></tr>
              ) : posts?.length > 0 ? (
                posts?.map((post) => (
                  <tr key={post.id}>
                    <Td>{post.id}</Td>
                    <Td alignLeft onClick={() => handlePostClick(post)}>
                      {post.title}
                    </Td>
                    <Td>{post.authorName}</Td>
                    <Td>{post.createdAt.substring(0, 10)}</Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <Td colSpan={6} style={{ padding: '50px 0', color: '#888' }}>
                    등록된 제보가 없습니다.
                  </Td>
                </tr>
              )}
            </tbody>
          </Table>
          <BottomArea>
              <PaginationWrapper>
                 {totalCount > 0 && (
                   <Pagination 
                     current={page}
                     total={totalCount}
                     pageSize={pageSize}
                     onChange={handlePageChange}
                     showSizeChanger={false}
                   />
                 )}
              </PaginationWrapper>
            <WriteButton onClick={handleWriteClick}>글쓰기</WriteButton>
          </BottomArea>
        </>
      ) : detailLoading ? (
        <LoadingContainer>
          <Spin size="large" tip="제보를 불러오는 중입니다..." />
        </LoadingContainer>
      ) : viewPost ? (
        <DetailContainer>
          <DetailHeader>
            <div className="top-row">
              <BackButton onClick={handleBackToList}>
                <ArrowLeftOutlined /> 목록으로
              </BackButton>
              <DetailActions>
                {(currentUser.role === 'ADMIN_MAIN' || currentUser.username === viewPost.authorName) && (
                  <>
                    <Button onClick={handleEditFromDetail}>수정</Button>
                    <ActionButton isDelete onClick={() => handleDelete(viewPost.id)}>삭제</ActionButton>
                  </>
                )}
              </DetailActions>
            </div>
            <DetailTitle>{viewPost.title}</DetailTitle>
            <DetailMeta>
              <span>작성자: {viewPost.authorName}</span>
              <span className="divider">|</span>
              <span>{viewPost.createdAt.substring(0, 10)}</span>
            </DetailMeta>
          </DetailHeader>

          <DetailContent>
            {/* 게시글 본문 */}
            <div className="text-content">{viewPost.content}</div>
            
            {/* 첨부된 이미지가 바로 렌더링되는 영역 */}
            {viewPost.files && viewPost.files.length > 0 && (
              <ImageGallery>
                {viewPost.files.map((file) => 
                  isImageFile(file.originFileName) ? (
                    <ImageViewer key={file.id} file={file} onDownload={handleDownload} />
                  ) : (
                    // 이미지 확장자가 아닌 파일이 혹시 있을 경우를 대비한 Fallback UI
                    <FileItem key={file.id} onClick={() => handleDownload(file)}>
                      <div className="file-info">
                        <PaperClipOutlined />
                        <span className="name">{file.originFileName}</span>
                        <span className="meta-info">
                          <span className="size">({formatFileSize(file.fileSize)})</span>
                        </span>
                      </div>
                      <DownloadOutlined className="download-icon" />
                    </FileItem>
                  )
                )}
              </ImageGallery>
            )}
          </DetailContent>

        </DetailContainer>
      ) : null}

      {/* 모달(글쓰기/수정) 부분은 변경사항 없음 */}
      {isWriteModalOpen && (
         <ModalOverlay onClick={closeWriteModal}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
               <ModalHeader>
                 <h3>{editingId ? "제보 수정" : "제보 등록"}</h3>
                 <CloseButton onClick={closeWriteModal}>&times;</CloseButton>
               </ModalHeader>
               <ModalBody>
                  <Label>제목</Label>
                  <StyledInput 
                     name="title"
                     value={formData.title} 
                     onChange={handleInputChange} 
                     placeholder="제목을 입력하세요"
                  />
                  
                  <Label>내용</Label>
                  <StyledTextArea 
                     name="content"
                     value={formData.content} 
                     onChange={handleInputChange} 
                     rows={8}
                     placeholder="내용을 입력하세요"
                  />

                  <Label>첨부파일 (이미지 전용)</Label>
                  <FileInputWrapper>
                     <input 
                       type="file" 
                       multiple 
                       accept="image/jpeg, image/png, image/gif, image/bmp, image/webp"
                       id="file-upload" 
                       style={{ display: 'none' }} 
                       onChange={handleFileChange}
                     />
                     <label htmlFor="file-upload" className="upload-btn">
                       <PictureOutlined /> 이미지 추가
                     </label>
                     <span className="info-text">여러 장의 이미지를 선택할 수 있습니다.</span>
                  </FileInputWrapper>

                  {newFiles.length > 0 && (
                    <UploadFileList>
                      <div className="list-title">추가된 이미지:</div>
                      {newFiles.map((file, index) => (
                        <div key={index} className="file-row">
                           <span>{file.name}</span>
                           <button onClick={() => removeNewFile(index)}>&times;</button>
                        </div>
                      ))}
                    </UploadFileList>
                  )}

                  {editingId && existingFiles.length > 0 && (
                    <UploadFileList>
                      <div className="list-title">기존 이미지:</div>
                      {existingFiles.map((file) => (
                        <div key={file.id} className="file-row existing">
                           <span><PictureOutlined style={{ marginRight: '5px' }} /> {file.originFileName}</span>
                           <button onClick={() => handleRemoveExistingFile(file.id)} className="delete-btn">
                             <DeleteOutlined />
                           </button>
                        </div>
                      ))}
                    </UploadFileList>
                  )}

               </ModalBody>
               <ModalFooter>
                  <Button onClick={closeWriteModal}>취소</Button>
                  <Button primary onClick={handleSubmit}>{editingId ? "수정" : "등록"}</Button>
               </ModalFooter>
            </ModalBox>
         </ModalOverlay>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%; max-width: 1000px; margin: 0 auto; padding: 40px 20px;
  font-family: 'Noto Sans KR', sans-serif; min-height: 500px;
`;

const LoadingContainer = styled.div`
  display: flex; justify-content: center; align-items: center; height: 400px; width: 100%;
`;

const TopBar = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;
`;

const TotalCount = styled.div`
  font-size: 14px; color: #333;
  span { color: ${SubColor}; font-weight: bold; }
`;

const SelectBox = styled.select`
  padding: 5px 10px; border: 1px solid #ddd; border-radius: 4px; outline: none; cursor: pointer;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse; border-top: 2px solid ${SubColor};
`;

const Th = styled.th<{ width?: string }>`
  padding: 15px 10px; background-color: #f9f9f9; border-bottom: 1px solid #ddd;
  font-size: 14px; font-weight: 600; color: #333; text-align: center;
  width: ${(props) => props.width || 'auto'};
`;

const Td = styled.td<{ alignLeft?: boolean }>`
  padding: 15px 10px; border-bottom: 1px solid #eee; font-size: 14px; color: #555;
  text-align: ${(props) => (props.alignLeft ? 'left' : 'center')};
  &:hover {
    color: ${(props) => (props.alignLeft ? SubColor : '#555')};
    font-weight: ${(props) => (props.alignLeft ? '600' : 'normal')};
    cursor: ${(props) => (props.alignLeft ? 'pointer' : 'default')};
    text-decoration: ${(props) => (props.alignLeft ? 'underline' : 'none')};
  }
`;

const BottomArea = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-top: 20px; position: relative;
`;

const PaginationWrapper = styled.div`
  flex: 1; display: flex; justify-content: center;
`;

const WriteButton = styled.button`
  padding: 10px 24px; background-color: #fff; border: 1px solid ${SubColor};
  color: ${SubColor}; font-size: 14px; font-weight: 600; cursor: pointer; border-radius: 4px;
  transition: all 0.2s;
  &:hover { background-color: ${SubColor}; color: #fff; }
`;

const DetailContainer = styled.div`
  background-color: #fff; border-radius: 8px; animation: fadeIn 0.3s;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const DetailHeader = styled.div`
  border-bottom: 1px solid #eee; padding-bottom: 20px; margin-bottom: 30px;
  .top-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; }
`;

const BackButton = styled.button`
  background: none; border: none; font-size: 15px; color: #555; cursor: pointer;
  display: flex; align-items: center; gap: 8px; padding: 0; font-weight: 500;
  &:hover { color: ${SubColor}; }
`;

const DetailActions = styled.div`
  display: flex; gap: 8px;
`;

const DetailTitle = styled.h2`
  font-size: 24px; color: #333; margin: 0 0 10px 0; font-weight: 700;
`;

const DetailMeta = styled.div`
  font-size: 14px; color: #888; display: flex; align-items: center; gap: 10px;
  .divider { color: #ddd; font-size: 12px; }
`;

const DetailContent = styled.div`
  font-size: 16px; color: #333; line-height: 1.8;
  min-height: 200px; margin-bottom: 50px; padding: 10px;

  .text-content {
    white-space: pre-wrap;
    margin-bottom: 40px;
  }
`;

/* 이미지 갤러리 관련 스타일 추가 */
const ImageGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 30px;
  border-top: 1px dashed #eee;
  padding-top: 30px;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  background: #f9f9f9;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  
  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  .image-overlay {
    position: absolute;
    top: 15px;
    right: 15px;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    
    button {
      background: rgba(0,0,0,0.65);
      color: white;
      border: none;
      padding: 8px 14px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;
      
      &:hover {
        background: rgba(0,0,0,0.85);
      }
    }
  }

  &:hover .image-overlay {
    opacity: 1;
  }
`;

const FileItem = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 16px; background-color: #f9f9f9; border: 1px solid #eee; border-radius: 6px;
  cursor: pointer; transition: all 0.2s; margin-bottom: 10px;
  
  &:hover { 
    background-color: #f0f8ff; border-color: ${SubColor}; 
    .download-icon { color: ${SubColor}; }
    .name { color: ${SubColor}; text-decoration: underline; }
  }

  .file-info {
    display: flex; align-items: center; gap: 8px; font-size: 14px; color: #555;
    flex-wrap: wrap;
    .name { font-weight: 500; }
    
    .meta-info {
       display: flex; align-items: center; gap: 6px;
       margin-left: 5px;
       .size { color: #888; font-size: 12px; }
    }
  }
  
  .download-icon { color: #aaa; font-size: 16px; }
`;

const FileInputWrapper = styled.div`
  margin-bottom: 10px;
  .upload-btn {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 6px 12px; border: 1px solid #ddd; border-radius: 4px;
    background: #fff; cursor: pointer; font-size: 13px; color: #555;
    &:hover { background: #f5f5f5; }
  }
  .info-text { margin-left: 10px; font-size: 12px; color: #999; }
`;

const UploadFileList = styled.div`
  background: #f9f9f9; border: 1px solid #eee; border-radius: 4px; padding: 10px; margin-bottom: 15px;
  .list-title { font-size: 12px; color: #888; margin-bottom: 5px; font-weight: 600; }
  .file-row {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 13px; padding: 4px 0; border-bottom: 1px dashed #eee;
    &:last-child { border-bottom: none; }
    button { 
      background: none; border: none; cursor: pointer; color: #999; 
      &:hover { color: red; }
    }
  }
  .existing { color: #333; }
`;

const ActionButton = styled.button<{ isDelete?: boolean }>`
  padding: 8px 16px; font-size: 14px; border: 1px solid #ddd; background-color: #fff;
  color: ${(props) => (props.isDelete ? '#ff4d4f' : '#333')};
  border-radius: 4px; cursor: pointer; transition: all 0.2s; font-weight: 500;
  &:hover {
    border-color: ${(props) => (props.isDelete ? '#ff4d4f' : SubColor)};
    color: ${(props) => (props.isDelete ? '#fff' : SubColor)};
    background-color: ${(props) => (props.isDelete ? '#ff4d4f' : '#fff')};
  }
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 8px 16px; border-radius: 4px; font-size: 14px; font-weight: 500; cursor: pointer;
  border: 1px solid ${(props) => (props.primary ? SubColor : '#ddd')};
  background-color: ${(props) => (props.primary ? SubColor : '#fff')};
  color: ${(props) => (props.primary ? '#fff' : '#333')};
  transition: all 0.2s;
  &:hover { opacity: 0.9; background-color: ${(props) => (props.primary ? SubColor : '#f0f0f0')}; }
`;

const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background-color: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center; z-index: 1000;
`;

const ModalBox = styled.div`
  background: white; width: 600px; max-width: 90%; max-height: 90vh;
  border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex; flex-direction: column; overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 16px 24px; border-bottom: 1px solid #eee;
  display: flex; justify-content: space-between; align-items: center;
  h3 { margin: 0; font-size: 18px; font-weight: 600; color: #333; }
`;

const CloseButton = styled.button`
  background: none; border: none; font-size: 24px; cursor: pointer; color: #999;
  &:hover { color: #333; }
`;

const ModalBody = styled.div`
  padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; flex: 1;
`;

const ModalFooter = styled.div`
  padding: 16px 24px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; gap: 10px; background-color: #f9f9f9;
`;

const Label = styled.label`
  font-size: 14px; font-weight: 600; color: #333; margin-bottom: 4px; display: block;
`;

const StyledInput = styled.input`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; outline: none; box-sizing: border-box;
  &:focus { border-color: ${SubColor}; }
`;

const StyledTextArea = styled.textarea`
  width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 14px; outline: none; resize: vertical; box-sizing: border-box;
  &:focus { border-color: ${SubColor}; }
`;

const AdminInfoBox = styled.div`
  margin-bottom: 24px;
  
  .ant-alert-info {
    background-color: #f0f7ff;
    border: 1px solid #adc6ff;
    border-radius: 8px;
  }
  
  .ant-alert-message {
    font-weight: bold;
    color: #1d39c4;
  }

  .ant-alert-description {
    color: #262626;
    font-size: 14px;
  }
`;

export default ErrorReport;